import { Icon } from '@sunwebgroup/uds-ui/icon'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { format, sub } from 'date-fns'
import * as React from 'react'
import { useState } from 'react'
import { useIntl } from '@/intl'
import { DriverForm } from '@/scopes/checkout/components/services/CarRentalFilteringService/Driver/DriverForm'
import {
  CarSelectedSheetCategoryText,
  CarSelectedSheetContent,
  CarSelectedSheetHintText,
  CarSelectedSheetTrailing,
  CarSelectedSheetTrigger,
  CarSelectedSheetTriggerContent,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/SheetViews'
import {
  useCarRentalFilteringServiceStore,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { useService } from '@/scopes/checkout/domain/hooks'
import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types'
import { DateFormat } from '@/scopes/common/domain/types'
import { parseDateForLocalFormatting } from '@/scopes/common/domain/utils/dates'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import styles from './Driver.module.css'

function formatBirthdate(birthdate: string | null | undefined, dateFormat: DateFormat) {
  if (!birthdate) {
    return ''
  }
  const parsed = parseDateForLocalFormatting(birthdate)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }
  return format(parsed, dateFormat)
}

export function DriverSheet({ openByDefault = false, ...option }: CheckoutCarRentalServiceOption & { openByDefault?: boolean }) {
  const { service } = useService(ServiceType.CarRentalFiltering)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { startEditingCar, endEditingCar, setCarToBeSelected,
  } = useCarRentalFilteringServiceStore()
  const [showDriverForm, setShowDriverForm] = useState(false)
  const { participants, configurations } = service || {}
  const { driverId, value: carValue, id: carId } = option
  const defaultDriverBirthdate = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver })
  const dateFormat = useConfiguration('dateFormat')

  const driver = driverId ? participants?.[driverId] : null
  React.useEffect(() => {
    if (openByDefault) {
      setTimeout(() => {
        setShowDriverForm(true)
      }, 300)
    }
  }, [setShowDriverForm, openByDefault])

  const handleClickDriverForm = () => {
    setShowDriverForm(true)
    startEditingCar(carId, {
      driverId: driverId ?? '',
      driverBirthDate: driver?.birthdate ? parseDateForLocalFormatting(driver.birthdate) : new Date(defaultDriverBirthdate),
      driverHasCreditCard: Boolean(driverId && driverId !== '0'),
      bookedEquipmentsWithQuantity: option.equipments.filter(({ id }) => option.selectedEquipments?.includes(id)).map(({ id, quantitySelected }) => {
        return {
          id,
          quantity: quantitySelected ?? 1,
        }
      }),
      isEditing: Boolean(driverId && driverId !== '0'),
    })
    sendEvent(
      createCarEvent({
        result: 'edit',
        type: carValue,
        description: 'edit button clicked',
        element: 'button',
      }),
    )
  }

  const driverBirthdateFormatted = formatBirthdate(driver?.birthdate, dateFormat)

  const handleEndEditing = () => {
    endEditingCar(carId)
    setShowDriverForm(false)
    setCarToBeSelected('')
  }

  if (!service)
    return null
  if (!option)
    return null
  if (!participants)
    return null

  return (
    <Sheet.Root
      open={showDriverForm}
      onOpenChange={(open) => {
        if (!open) {
          handleEndEditing()
        }
      }}
    >
      <Sheet.Trigger>
        <CarSelectedSheetTrigger>
          <ListItem variant="tinted">
            <CarSelectedSheetTriggerContent onClick={handleClickDriverForm}>
              <Icon glyph="SteeringWheel" size={32} />
              <CarSelectedSheetContent>
                <CarSelectedSheetCategoryText variant="body-s-regular-tight">{intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_driver_overline' })}</CarSelectedSheetCategoryText>
                <CarSelectedSheetCategoryText className={styles.driverName} variant="body-m-bold-tight">{driver ? driver.name : intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_driver_select_cell' })}</CarSelectedSheetCategoryText>
                <CarSelectedSheetHintText variant="body-s-regular-tight">
                  {driver ? intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_driver_birthdate_hint' }, { birthdate: driverBirthdateFormatted }) : intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_driver_hint' })}
                </CarSelectedSheetHintText>
              </CarSelectedSheetContent>
              <CarSelectedSheetTrailing>
                <Icon glyph="ChevronRight" size={24} />
              </CarSelectedSheetTrailing>
            </CarSelectedSheetTriggerContent>
          </ListItem>
        </CarSelectedSheetTrigger>
      </Sheet.Trigger>
      <Sheet.Body side="right" hasPadding bottomSideInMobile>
        <Sheet.Header titleLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_driver_sheet_title' })} closeLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_sheet_cancel_text' })} />
        <DriverForm {...option} showDriverForm setShowDriverForm={setShowDriverForm} />
      </Sheet.Body>
    </Sheet.Root>
  )
}
